<template>
    <PageWrapper :breadcrumb-links="breadcrumbLinks">
        <main
            class="mx-auto w-full max-w-7xl px-2 lg:pl-[calc(theme(spacing.64)+theme(spacing.4))]"
        >
            <StickyPanel
                :top-default="0"
                :top-responsive="10"
            >
                <FilterToolbar
                    :key="`template-filter-toolbar-` + pager.key"
                    :page-data="pageData"
                    :buttons="toolbarConfig.buttons"
                />
                <OrderBar
                    :current="pageData.sortBy"
                    :order-options="pageData.sortOptions"
                />
            </StickyPanel>
            <AlertBox
                v-if="pageData.data.items.length === 0 && pageData.loading === false"
                state="warning"
                :icon="MdiInformation"
                :title="t('Templates')"
            >
                <p class="py-1 text-sm">{{ t('notFoundText') }}</p>
            </AlertBox>

            <div
                v-if="pageData.data.items.length > 0"
                :key="`page-data-list-${pageDataListKey}`"
                class="mt-4 grid grid-cols-1 gap-4 p-0.5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
            >
                <div
                    v-for="(template, index) in pageData.data.items"
                    :key="index"
                    class="group/folder cursor-pointer rounded-md border border-gray-200 p-4 shadow-sm hover:bg-gray-100"
                    @click="requestDirectory(templateDirectoryName(template.response.templateId))"
                >
                    <MdiFolderOutline
                        class="block h-12 w-12 text-gray-400 group-hover/folder:hidden group-hover/folder:text-blue-400"
                    />
                    <MdiFolderOpenOutline
                        class="hidden h-12 w-12 text-gray-400 group-hover/folder:block group-hover/folder:text-blue-600"
                    />

                    <div
                        class="text-sm font-medium break-words text-gray-700 group-hover/folder:text-blue-600"
                    >
                        {{ template.response.name }}
                    </div>
                </div>
            </div>
        </main>
    </PageWrapper>
    <div
        class="fixed right-0 bottom-0 left-1 z-2000 flex h-16 items-center justify-center bg-white lg:left-64"
    >
        <Pager
            :key="pager.key"
            :current="pager.current"
            :end="pager.end"
        />
    </div>
    <Spinner
        v-if="pageData.loading === true"
        type="dots"
        :color="`text-blue-600`"
        :color2="`text-blue-400`"
        :text-color="`text-gray-200`"
        :overlay="true"
        :speed="1000"
        :dots="true"
        :dot-char="'.'"
        :dot-dual="false"
        :dot-reverse="false"
        :text="t('loading')"
        :inline="false"
        :pulse="true"
    />
</template>

<script setup>
import { reactive, watch, inject, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Pager from '../ui/Pager.vue';
import { makePager } from '../composables/usePaginator';
import PageWrapper from '../ui/PageWrapper.vue';
import FilterToolbar from '../ui/FilterToolbar.vue';
import Spinner from '../ui/Spinner.vue';
import AlertBox from '../ui/AlertBox.vue';
import { t } from '../composables/i18n';
//import { sortArrayOfObjectsBy } from '../composables/utils';
import { getTemplates } from '../composables/useTemplates';
import StickyPanel from '../ui/StickyPanel.vue';
import { templateDirectoryName } from '../composables/useFiles';
import { reloadPageData } from '../composables/usePages';
import { parseSortBy, sortByChanged } from '../composables/useSortBy';
import OrderBar from '../ui/OrderBar.vue';
import { getRndKey } from '../composables/utils';
import {
    MdiFolderOutline,
    MdiFolderOpenOutline,
    MdiInformation,
} from 'materialdesignicons-vue3/icons/';

const route = useRoute();
const router = useRouter();
const config = inject('config');
const sortDefault = ['name:asc'];
const pageDataListKey = ref(getRndKey(17));
const pageData = reactive({
    data: {
        items: [],
        totalCount: 0,
    },
    currentPage: parseInt(route.params.page) || 1,
    perPage: 60,
    search: route.query.search || '',
    type: route.query.type || '',
    loading: true,
    useTags: false,
    sortDefault,
    sortBy: parseSortBy(route.query.sortBy, sortDefault),
    sortOptions: [
        /* { name: t('sortId'), value: 'id' }, */
        { name: t('sortName'), value: 'name' },
        /* { name: t('sortCode'), value: 'code' }, */
        { name: t('sortCreated'), value: 'created' },
        { name: t('sortUpdated'), value: 'updated' },
    ],
    filters: {
        type: {
            // active value
            active: route.query.type || '',
            name: t('filterType'),
            values: [
                {
                    value: '',
                    name: t('filterAll'),
                },
                ...config.templates.allowTypes.map((type) => {
                    return {
                        value: type,
                        name: type,
                    };
                }),
            ],
        },
    },
});

const pager = reactive(makePager(pageData.currentPage, pageData.data.totalCount, pageData.perPage));

const breadcrumbLinks = reactive([
    {
        name: 'dashboard',
        title: t('Dashboard'),
    },
    {
        name: 'files',
        title: `${t('Files')} (${pageData.data.totalCount})`,
        final: true,
    },
]);

const toolbarConfig = reactive({
    buttons: [],
});
//validDirs = sortArrayOfObjectsBy(validDirs, 'name');

const refreshPageData = async () => {
    await reloadPageData(pageData, getTemplates, pager);
    breadcrumbLinks[1].title = `${t('Files')} (${pageData.data.totalCount})`;
    pageDataListKey.value = getRndKey(17);
};

const requestDirectory = (directory) => {
    router.push({
        name: 'directory',
        params: {
            dirname: directory,
        },
    });
};

watch(
    () => route.params.page,
    async (newPage, oldPage) => {
        if (newPage !== oldPage) {
            //console.log('Route page changed', newPage, oldPage);
            pageData.currentPage = parseInt(newPage) || 1;
            await refreshPageData();
        }
    },
);

watch(
    () => route.query,
    async (newQuery, oldQuery) => {
        const filterKeys = Object.keys(pageData.filters);
        const searchChanged = newQuery.search !== oldQuery.search;
        const filtersChanged = filterKeys.some((key) => newQuery[key] !== oldQuery[key]);
        const sortChanged = sortByChanged(newQuery, oldQuery);
        if (searchChanged || filtersChanged || sortChanged) {
            //console.log('Query changed', newQuery, oldQuery);
            pageData.search = newQuery.search || '';
            for (const key of filterKeys) {
                pageData.filters[key].active = newQuery[key] || '';
            }
            if (sortChanged) {
                pageData.sortBy = parseSortBy(newQuery.sortBy, pageData.sortDefault);
            }
            pageData.currentPage = 1;
            await refreshPageData();
        }
    },
);

onMounted(async () => {
    if (route.query.tag) {
        pageData.selectedTags = Array.isArray(route.query.tag)
            ? route.query.tag
            : [route.query.tag];
    }
    await refreshPageData();
});
</script>
